import {
  type ApiErrorOverride,
  type TFunction,
  type TranslateApiErrorOptions,
  translateApiError as translateApiErrorCore,
} from '@jq4race/i18n';

/**
 * translateApiError (admin) — thin wrapper over the shared translator in
 * `@jq4race/i18n`, which resolves ANY backend error code through the
 * `apiErrors:codes.<code>` convention (see `packages/i18n/src/api-errors/api-error-messages.ts`) and never
 * renders the backend's raw `message`.
 *
 * What lives here: only the admin-specific rules the convention cannot
 * express — a `context` variant of the copy, or a status filter. Everything
 * else (the 403/400/422/409 flavours of `forbidden`, zone/location capacity
 * numbers read from `details`, ...) is resolved by the shared catalogue; no
 * call site needs to change when a new backend code appears, only
 * `packages/i18n/src/api-errors/api-error-messages.ts` does (apiErrorCatalogue.test.ts fails otherwise).
 *
 * Pass `{ fallback: t('feature.loadError') }` so a failure the catalogue does
 * not know still reads as the action that failed instead of a bare generic.
 */

export type { TranslateContext } from '@jq4race/i18n';

const ERROR_TRANSLATIONS: Partial<Record<string, ApiErrorOverride>> = {
  // QA R2-02 — invitations: the e-mail is globally unique (it includes
  // deactivated users) and only one pending invitation may exist per e-mail.
  // The default copy is the org-admin wording (users list has Reactivar / Invitaciones tab);
  // the super-admin invite dialogs (context 'platform') get a generic message instead.
  duplicate_email: {
    key: 'apiErrors:duplicateEmail',
    contextKeys: { platform: 'apiErrors:duplicateEmailPlatform' },
    statuses: [409],
  },
  duplicate_invitation: {
    key: 'apiErrors:duplicateInvitation',
    contextKeys: { platform: 'apiErrors:duplicateInvitationPlatform' },
    statuses: [409],
  },
};

export function translateApiError(
  error: unknown,
  t: TFunction,
  options: Omit<TranslateApiErrorOptions, 'overrides'> = {},
): string {
  return translateApiErrorCore(error, t as never, { ...options, overrides: ERROR_TRANSLATIONS });
}
